import React, { useEffect, useState } from 'react'
import { graphql } from 'gatsby'
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer'

import Seo from '../components/seo'
import Layout from '../components/layout'
import NewsroomLayout from '../components/layouts/newsroom'

import pageAlternatesUrls from '../utils/pageAlternatesUrls'

function Newsroom({data, pageContext, location}) {
  const mainContent = data.mainContent
  const urls = pageAlternatesUrls(mainContent, data.alternateContent, 'slug', null, null, pageContext.locale)
  const pressReleases = data.pressReleases.nodes.filter(item => item.pressRelease !== null)
  const [isFirstRun, setIsFirstRun ] = useState(true)

  let page = pageContext.page + 1
  let currentUrl = (pageContext.locale === 'fr') ? urls.frUrl : urls.enUrl

  useEffect(() => {
    if (isFirstRun) {
      setIsFirstRun(false)
    }
  }, [isFirstRun])

  let allNews = data.allNews.nodes.concat(data.allSocials.nodes)
  allNews = allNews.sort((a, b) => a.date.localeCompare(b.date)).reverse()
  
  return (
    <Layout 
      frUrl={urls.frUrl}
      enUrl={urls.enUrl} 
      initLocale={pageContext.locale}
      mainTag={mainContent.mainTag.title}
      headerType="bg-white"
    >
      <Seo
        frUrl={urls.frUrl}
        enUrl={(page <= pageContext.maxCommonPages) ? urls.enUrl : null}
        newsroomPage={page}
        page={{
          title: mainContent.seoTitle ? mainContent.seoTitle : mainContent.title,
          description: mainContent.seoDescription ? mainContent.seoDescription.seoDescription : documentToPlainTextString(JSON.parse(mainContent.chapo.raw)),
          path: currentUrl
        }}
      />

      <NewsroomLayout 
        currentUrl={currentUrl}
        data={mainContent}
        events={data.events.nodes}
        news={data.news.nodes}
        socials={data.allSocials.nodes}
        allNews={allNews}
        documents={data.documents.nodes}
        pressReleases={pressReleases}
        page={page}
        isFirstRun={isFirstRun}
        furtherContent={data.furtherContent} />
    </Layout>
  );
}

export const pageQuery = graphql`
  query SingleNewsroomList($contentful_id: String!, $locale: String!) {
    mainContent: contentfulNewsroomList(contentful_id: { eq: $contentful_id }, node_locale: { eq: $locale }) {
      title
      internal {
        type
      }
      seoTitle
      seoDescription {
        seoDescription
      }
      slug
      node_locale
      mainTag {
        title
      }
      breadcrumbCurrentLabel
      type
      chapo {
        raw
        references {
          __typename
          ... on ContentfulStyledText {
            contentful_id
            text
            type
          }
          ... on ContentfulNewsroomList {
            contentful_id
            title
            slug
            node_locale
            internal {
              type
            }
          }
          ... on ContentfulNews {
            contentful_id
            title
            node_locale
            internal {
              type
            }
          }
          ... on ContentfulSolution {
            contentful_id
            title
            node_locale
            internal {
              type
            }
          }
          ... on ContentfulLanding {
            contentful_id
            title
            slug
            node_locale
            internal {
              type
            }
          }
          ... on ContentfulOffer {
            contentful_id
            solution {
              title
            }
            title
            node_locale
            internal {
              type
            }
          }
          ... on ContentfulEvent {
            contentful_id
            title
            node_locale
            internal {
              type
            }
          }
          ... on ContentfulContact {
            contentful_id
            title
            slug
            node_locale
            internal {
              type
            }
          }
          ... on ContentfulSimulator {
            contentful_id
            title
            slug
            node_locale
            internal {
              type
            }
          }
          ... on ContentfulHub {
            contentful_id
            title
            slug
            node_locale
            internal {
              type
            }
          }
          ... on ContentfulHomepageSolutions {
            contentful_id
            title
            node_locale
            internal {
              type
            }
          }
          ... on ContentfulPage {
            contentful_id
            title
            slug
            customType
            node_locale
            internal {
              type
            }
          }
          ... on ContentfulAsset {
            contentful_id
            file {
              url
            }
          }
        }
      }
      pushPages {
        chapo {
          chapo
        }
        ctaLabel
        page {
          ... on Node {
            ... on ContentfulLanding {
              internal {
                type
              }
              mainTag {
                title
              }
              title
              slug
              node_locale
            }
            ... on ContentfulOffer {
              internal {
                type
              }
              mainTag {
                title
              }
              solution {
                title
              }
              title
              node_locale
            }
            ... on ContentfulSolution {
              internal {
                type
              }
              mainTag {
                title
              }
              title
              node_locale
            }
            ... on ContentfulSimulator{
              internal {
                type
              }
              mainTag {
                title
              }
              title
              node_locale
              mainImage {
                altImage
                file {
                  file {
                    url
                  }
                }
              }
            }
            ... on ContentfulEvent {
              internal {
                type
              }
              mainTag {
                title
              }
              title
              node_locale
              mainImage {
                altImage
                file {
                  file {
                    url
                  }
                }
              }
            }
            ... on ContentfulNewsroomList {
              internal {
                type
              }
              mainTag {
                title
              }
              title
              slug
              node_locale
            }
            ... on ContentfulNews {
              internal {
                type
              }
              mainTag {
                title
              }
              title
              publicationDate
              node_locale
              mainImage {
                altImage
                file {
                  file {
                    url
                  }
                }
              }
            }
            ... on ContentfulPage {
              internal {
                type
              }
              mainTag {
                title
              }
              title
              slug
              customType
              node_locale
              mainImage {
                altImage
                file {
                  file {
                    url
                  }
                }
              }
            }
            ... on ContentfulContact {
              internal {
                type
              }
              mainTag {
                title
              }
              title
              slug
              node_locale
              mainImage {
                altImage
                file {
                  file {
                    url
                  }
                }
              }
            }
            ... on ContentfulSimulator {
              title
              mainTag {
                title
              }
              slug
              node_locale
              internal {
                type
              }
              mainImage {
                altImage
                file {
                  file {
                    url
                  }
                }
              }
            }
            ... on ContentfulHub {
              internal {
                type
              }
              mainTag {
                title
              }
              title
              slug
              node_locale
            }
          }
        }
      }
    }

    events: allContentfulEvent(sort: {order: ASC, fields: startDate}, filter: {distributionChannel: {elemMatch: {title: {eq: "Terega solutions"}}}, fields: {isFuture: {eq: true}}, node_locale: {eq: $locale}, title: {ne: null}}) {
      nodes {
        title
        internal {
          type
        }
        profile {
          title
        }
        node_locale
        mainImage {
          altImage
          file {
            file {
              url
            }
          }
        }
       startDate
       endDate
      }
    }

    news: allContentfulNews(filter: {distributionChannel: {elemMatch: {title: {eq: "Terega solutions"}}}, node_locale: {eq: $locale}, title: {ne: null}}, limit: 9, sort: {fields: publicationDate, order: DESC}) {
      nodes {
        title
        node_locale
        internal {
          type
        }
        profile {
          title
        }
        publicationDate
        createdAt
        mainImage {
          altImage
          file {
            file {
              url
              details {
                image {
                  height
                  width
                }
              }
            }
          }
        }
      }
    }

    documents: allContentfulDocument(sort: {order: DESC, fields: publicationDate}, filter: {distributionChannel: {elemMatch: {title: {eq: "Terega solutions"}}}, node_locale: {eq: $locale}, title: {ne: null}}) {
      nodes {
        title
        type
        profile: profileBis {
          title
        }
        node_locale
        pdf {
          file {
            url
            details {
              size
            }
          }
        }
      }
    }

    pressReleases: allContentfulNews(sort: {order: DESC, fields: publicationDate}, filter: {distributionChannel: {elemMatch: {title: {eq: "Terega solutions"}}}, node_locale: {eq: $locale}, title: {ne: null}}) {
      nodes {
        title
        node_locale
        internal {
          type
        }
        profile {
          title
        }
        node_locale
        publicationDate
        pressRelease {
          annexes {
            file {
              url
            }
          }
          pdf {
            file {
              url
              details {
                size
              }
            }
            title
          }
        }
      }
    }
    
    allNews: allContentfulNews(filter: {distributionChannel: {elemMatch: {title: {eq: "Terega solutions"}}}, node_locale: {eq: $locale}, title: {ne: null}}, sort: {fields: publicationDate, order: DESC}) {
      nodes {
        title
        internal {
          type
        }
        profile {
          title
        }
        node_locale
        date: publicationDate
      }
    }

    allSocials: allSocials(sort: {fields: external_created_at, order: DESC}, filter: {source: {source: {ne: "Facebook"}}, message: {ne: "dummy"}}) {
      nodes {
        message
        external_created_at
        date: external_created_at
        full_url
        source {
          name
          source
          term
        }
      }
    }

    alternateContent: contentfulNewsroomList(contentful_id: { eq: $contentful_id }, node_locale: { ne: $locale }, title: {ne: null}) {
      title
      slug
      node_locale
    }

    furtherContent: allContentfulPage(filter: {distributionChannel: {elemMatch: {title: {eq: "Terega solutions"}}}, contentful_id: { ne: $contentful_id }, node_locale: {eq: $locale},  title: {ne: null}}, limit: 3, sort: {fields: createdAt, order: DESC}) {
      nodes {
        title
        slug
        internal {
          type
        }
        mainTag {
          title
        }
        mainImage {
          altImage
          file {
            file {
              url
            }
          }
        }
        chapo {
          raw
          references {
            __typename
            ... on ContentfulStyledText {
              contentful_id
              text
              type
            }
            ... on ContentfulNewsroomList {
              contentful_id
              title
              slug
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulNews {
              contentful_id
              title
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulSolution {
              contentful_id
              title
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulLanding {
              contentful_id
              title
              slug
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulOffer {
              solution {
                title
              }
              contentful_id
              title
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulEvent {
              contentful_id
              title
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulContact {
              contentful_id
              title
              slug
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulSimulator {
              contentful_id
              title
              slug
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulHub {
              contentful_id
              title
              slug
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulHomepageSolutions {
              contentful_id
              title
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulPage {
              contentful_id
              title
              slug
              customType
              node_locale
              internal {
                type
              }
            }
            ... on ContentfulAsset {
              contentful_id
              file {
                url
              }
            }
          }
        }
        node_locale
      }
    }
  }
`;

export default Newsroom